/**
 * Utility for handling Clerk organization invite tokens
 * Captures invite tokens from URLs and manages the invitation flow
 */

export interface InviteTokenData {
  token: string;
  capturedAt: number;
  organization?: string;
  redirectUrl?: string;
  companyId?: string;
}

const INVITE_TOKEN_KEY = 'clerk_invite_token';
const TOKEN_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Capture invite token from URL parameters
 * Should be called on page load to check for invite tokens
 */
export function captureInviteToken(): InviteTokenData | null {
  // Check for various possible invite token parameters
  const urlParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;

  let token =
    urlParams.get('invite_token') ||
    urlParams.get('invitation_token') ||
    urlParams.get('token') ||
    urlParams.get('__clerk_invitation_token') ||
    urlParams.get('invitation');

  // If no token in URL params, check hash for Clerk's routing
  if (!token && hash) {
    // Extract token from hash-based routing
    const hashParams = new URLSearchParams(hash.replace('#', '').split('?')[1] || '');
    token =
      hashParams.get('invite_token') ||
      hashParams.get('invitation_token') ||
      hashParams.get('__clerk_invitation_token') ||
      hashParams.get('invitation');
  }

  // Special handling for Clerk's default invitation flow
  if (
    !token &&
    (window.location.pathname.includes('sign-in') || window.location.pathname.includes('sign-up'))
  ) {
    // Check if we're in a Clerk invitation flow context
    // Look for any indication this is an invitation
    if (
      urlParams.get('redirect_url')?.includes('invitation') ||
      urlParams.get('after_sign_in_url')?.includes('invitation') ||
      hash.includes('invitation')
    ) {
      // Create a placeholder token to trigger invitation flow
      token = 'clerk_invitation_flow';
    }
  }

  if (!token) return null;

  const inviteData: InviteTokenData = {
    token,
    capturedAt: Date.now(),
    organization: urlParams.get('organization') || urlParams.get('org_name') || undefined,
    redirectUrl: urlParams.get('redirect_url') || urlParams.get('after_sign_in_url') || undefined,
    companyId: urlParams.get('company_id') || undefined,
  };

  // Store in sessionStorage (persists across redirects but not browser sessions)
  sessionStorage.setItem(INVITE_TOKEN_KEY, JSON.stringify(inviteData));

  console.log('Captured invite token:', {
    token: token.substring(0, 10) + '...',
    organization: inviteData.organization,
  });

  return inviteData;
}

/**
 * Get stored invite token if it exists and hasn't expired
 */
export function getStoredInviteToken(): InviteTokenData | null {
  try {
    const stored = sessionStorage.getItem(INVITE_TOKEN_KEY);
    if (!stored) return null;

    const inviteData: InviteTokenData = JSON.parse(stored);

    // Check if token has expired
    if (Date.now() - inviteData.capturedAt > TOKEN_EXPIRY_MS) {
      sessionStorage.removeItem(INVITE_TOKEN_KEY);
      return null;
    }

    return inviteData;
  } catch (error) {
    console.error('Error retrieving stored invite token:', error);
    sessionStorage.removeItem(INVITE_TOKEN_KEY);
    return null;
  }
}

/**
 * Clear stored invite token after processing
 */
export function clearInviteToken(): void {
  sessionStorage.removeItem(INVITE_TOKEN_KEY);
}

/**
 * Check if current URL contains invite token parameters
 */
export function hasInviteTokenInUrl(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;

  // Check URL parameters
  const hasUrlToken = !!(
    urlParams.get('invite_token') ||
    urlParams.get('invitation_token') ||
    urlParams.get('token') ||
    urlParams.get('__clerk_invitation_token') ||
    urlParams.get('invitation')
  );

  // Check hash parameters (Clerk sometimes uses hash-based routing)
  const hasHashToken =
    hash.includes('invitation') ||
    hash.includes('invite') ||
    hash.includes('__clerk_invitation_token');

  return hasUrlToken || hasHashToken;
}

/**
 * Clean invite token parameters from URL without page reload
 */
export function cleanInviteTokenFromUrl(): void {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Remove invite-related parameters
  params.delete('invite_token');
  params.delete('invitation_token');
  params.delete('token');
  params.delete('organization');
  params.delete('redirect_url');

  // Update URL without page reload
  window.history.replaceState({}, '', url.toString());
}
